import { GlobalCopilotFloatieButton } from '@ms/yammer-copilot/dist/integration';
import { useIsTopLevelFeaturePage } from '@ms/yammer-hooks/dist/navigation';
import { useTranslations } from '@ms/yammer-i18n-react';
import { useEndActiveSession } from '@ms/yammer-search-components/dist/telemetry';
import CommonStrings from '@ms/yammer-strings/dist/Common';
import PanesStrings from '@ms/yammer-strings/dist/Panes';
import { MultiColumnAppLayout } from '@ms/yammer-web-core-ui/dist/components/AppLayout';
import { AppLayoutBreakpoint, useContainerWidth } from '@ms/yammer-web-core-ui/dist/util/responsiveness';
import { tokens } from '@ms/yammer-web-core-ui/dist/util/theme/fluent9';
import {
  articleBasePath,
  articleEditPageMatchPath,
  articleNewMatchPath,
  articlePageMatchPath,
} from '@ms/yammer-web-support/dist/urls';
import { useUIFeatures } from '@ms/yammer-web-ui-features/dist/features';
import { useTeamsClientContext } from 'components/TeamsClientProvider/hooks';
import type { FC, PropsWithChildren } from 'react';
import React from 'react';
import { useRouteMatch } from 'react-router-dom';

import { AppLeftNav } from './AppLeftNav';
import ArticleFooter from './LoadableArticleFooter';
import LoadableTeamsSubHeader from './LoadableTeamsSubHeader';
import { useBackButtonProps, useGetBanners } from './hooks';

const AppLayout: FC<PropsWithChildren> = ({ children }) => {
  useEndActiveSession();
  /**
   * This custom Article logic is temporary until we refactor CoreUI's AppLayout so
   * content/page-level templates allow slot props like `footer`.
   * https://dev.azure.com/yammer/engineering/_workitems/edit/295328
   */
  const isAnyArticlesPage = !!useRouteMatch(articleBasePath);
  const isArticleCreatePage = !!useRouteMatch({ path: articleNewMatchPath, exact: true });
  const isArticleEditPage = !!useRouteMatch(articleEditPageMatchPath);
  const isSuggestedArticlePage = !!useRouteMatch({ path: '/groups/:groupId/suggested-article', exact: true });
  const isArticlePage = !!useRouteMatch(articlePageMatchPath) && !isArticleEditPage;
  const backgroundColor = isAnyArticlesPage || isSuggestedArticlePage ? tokens.colorNeutralBackground1 : undefined;
  const footer = isArticlePage ? <ArticleFooter /> : undefined;

  const width = useContainerWidth();
  const isCompact = width < AppLayoutBreakpoint.Width1032;

  const scrollPaddingTop = isArticleEditPage || isArticleCreatePage ? '120px' : '0px';
  const scrollPaddingBottom = (isArticleEditPage || isArticleCreatePage) && !isCompact ? '113px' : '0px';

  const { shouldHideSubHeader } = useTeamsClientContext();
  const subHeader = !shouldHideSubHeader ? <LoadableTeamsSubHeader /> : null;

  const banners = useGetBanners();

  const isLeftSidebarEnabled = useUIFeatures((features) => features.navigation?.leftSidebar);

  const leftNav = isLeftSidebarEnabled ? <AppLeftNav /> : null;

  const openAriaLabel = useTranslations(PanesStrings).showAdditionalInformationPane();
  const commonStrings = useTranslations(CommonStrings);
  const backButtonAriaLabel = commonStrings.commonGoBack();
  const closeButtonAriaLabel = commonStrings.commonClose();

  const isTopLevelFeaturePage = useIsTopLevelFeaturePage();
  const { goBack, showButton: hasBackButton } = useBackButtonProps();

  return (
    <MultiColumnAppLayout
      header={<GlobalCopilotFloatieButton />}
      headerDepth="depth0"
      headerBackgroundColor={tokens.colorNeutralBackground2}
      subHeader={subHeader}
      banner={banners}
      backButtonAriaLabel={backButtonAriaLabel}
      hasBackButton={hasBackButton}
      onBackButtonClick={goBack}
      leftNav={leftNav}
      hasHeroBackground={isTopLevelFeaturePage}
      openContentSidebarAriaLabel={openAriaLabel}
      closeContentSidebarAriaLabel={closeButtonAriaLabel}
      footer={footer}
      backgroundColor={backgroundColor}
      scrollPaddingTop={scrollPaddingTop}
      scrollPaddingBottom={scrollPaddingBottom}
    >
      {children}
    </MultiColumnAppLayout>
  );
};

export default AppLayout;
